import React, { useState, useEffect, useCallback, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Paper,
    Avatar,
    IconButton,
    Divider,
    Dialog,
    Card,
    Popover,
    Tooltip,
    Chip,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    MenuItem,
    Select,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
} from "@mui/material";
import { InsertEmoticon, Image, Clear, MenuBook, ExpandMore } from "@mui/icons-material";
import type { RootState, AppDispatch } from "../store";
import { resetPosts } from "../store/slices/posts";
import type { IRecipeWithId } from "../types/recipe";
import axiosService from "../utils/axios";
import ImageUpload from "./ImageUpload";
import PostInput from "./PostInput";
import EmojiPicker from "emoji-picker-react";
import type { EmojiClickData } from "emoji-picker-react";
import RecipePicker from "./RecipePicker";
import PostCardFeed from "./PostCardFeed";
import LoadingButton from "./LoadingButton";
import Theme from "../utils/theme";
import { useRefreshPosts } from "../hooks/useRefreshPosts";
import { visibilityOptions, visibilityLabels } from "../utils/helpers/recipeHelper";
import Loading from "./Loading";

// *** Memoized Components *** //
const MemoizedPostCardFeed = memo(PostCardFeed);

// *** Component specific types *** //
interface IFiles {
    file: File;
    caption?: string;
}

// ***** Main Component ***** //
const CommunityFeed: React.FC = () => {
    // *** Root States *** //
    const authState = useSelector((state: RootState) => state.auth);
    const targetUsername = authState.account ? authState.account.username : undefined;

    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const dispatch = useDispatch<AppDispatch>();

    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    // *** Component States *** //
    const [postInputText, setPostInputText] = useState("");
    const [postImages, setPostImages] = useState<string[]>([]);
    const [postRecipes, setPostRecipes] = useState<IRecipeWithId[]>([]);
    const [feedLoading, setFeedLoading] = useState(false);
    const [openImageUploadModal, setOpenImageUploadModal] = useState(false);
    const [openRecipeUploadModal, setOpenRecipeUploadModal] = useState(false);
    const [emojiPicker, setEmojiPicker] = useState<null | (EventTarget & HTMLElement)>(null);
    const [files, setFiles] = useState<IFiles[]>([]);
    const [posting, setPosting] = useState(false);
    const [followedOnly, setFollowedOnly] = useState(false);
    const [visibility, setVisibility] = useState<"public" | "private" | "personal">("private");
    const [visibilityLoading, setVisibilityLoading] = useState(false);

    // *** Constant Data ** //
    const refreshPosts = useRefreshPosts(setFeedLoading);

    // *** Use Effects *** //
    useEffect(() => {
        // Reset the posts on component re-render //
        return () => {
            dispatch(resetPosts());
        };
    }, [dispatch]);

    useEffect(() => {
        // If user is not logged in then do not retrieve settings
        if (!targetUsername) return;

        setVisibilityLoading(true);

        // Retrieve and set default post visibility //
        axiosService
            .get(`${process.env.REACT_APP_API_URL}/users/settings`)
            .then((res) => {
                setVisibility(res.data.body.defaultPostVisibility);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setVisibilityLoading(false);
            });
    }, [targetUsername]);

    // *** Component Helpers *** //

    // Open emoji picker //
    const handleEmojiPicker = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setEmojiPicker(event.currentTarget);
    }, []);

    // Close emoji picker//
    const handleCloseEmojiPicker = useCallback(() => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        setEmojiPicker(null);
    }, []);

    // Select emoji from emoji picker //
    const handleEmojiClick = useCallback((emojiData: EmojiClickData) => {
        setPostInputText((prev) => prev + emojiData.emoji);
    }, []);

    // Render emoji picker //
    const emojiPickerElement = useMemo(() => <EmojiPicker onEmojiClick={handleEmojiClick} height={450} />, [handleEmojiClick]);

    // Post input on-change //
    const handlePostInputChange = useCallback((value: string) => {
        setPostInputText(value);
    }, []);

    // Add an image through the post input //
    const addImage = useCallback((previewUrl: string | null, data: { file: File | null; caption?: string }) => {
        // If there is no preview or no file parameters then return //
        if (previewUrl === null || data.file === null) {
            setOpenImageUploadModal(false);
            return;
        }

        const previewFile = data.file;

        // Set image states accordingly //
        setFiles((prev) => [...prev, { file: previewFile, caption: data.caption }]);
        setPostImages((prev) => [...prev, previewUrl]);
        setOpenImageUploadModal(false);
    }, []);

    // Return image related states back to default ([]) //
    const removeImage = useCallback(() => {
        setPostImages([]);
        setFiles([]);
    }, []);

    // Add recipes through state related to post input //
    const addRecipes = useCallback((savedRecipes: IRecipeWithId[], selectedRecipes: Set<string>) => {
        const postedRecipes = savedRecipes.filter((recipe) => {
            if (selectedRecipes.has(recipe._id)) return true;

            return false;
        });

        setPostRecipes(postedRecipes);
        setOpenRecipeUploadModal(false);
    }, []);

    // Remove recipe from state related to post input //
    const removeRecipe = useCallback((removeId: string) => {
        setPostRecipes((prev) => prev.filter((item: IRecipeWithId) => item._id !== removeId));
    }, []);

    // Reset all states related to post input //
    const resetPostInput = useCallback(() => {
        setPostInputText("");
        setFiles([]);
        setPostImages([]);
        setPostRecipes([]);
    }, []);

    // Send a post //
    const createPost = useCallback(() => {
        setPosting(true);
        const postRecipeIds = postRecipes.map((recipe) => recipe._id);

        axiosService
            .post(`${process.env.REACT_APP_API_URL}/posts/create`, {
                textContent: postInputText,
                recipeIds: postRecipeIds,
                visibility,
            })
            .then((res) => {
                // If there are no files attached, then reset the post input states and return //
                if (files.length === 0) {
                    refreshPosts({
                        username: authState.account?.username,
                        followedOnly,
                        onDone: () => {
                            resetPostInput();
                            setPosting(false);
                        },
                    });
                    return;
                }

                // Place the image files and captions in a form data //
                const formData = new FormData();
                formData.append("postId", res.data.body.postId);
                files.forEach((file) => {
                    formData.append("images", file.file);

                    const captionToSend = file.caption ?? "";
                    formData.append("captions", captionToSend);
                });

                // Upload the file after sending the post //
                axiosService
                    .post(`${process.env.REACT_APP_API_URL}/posts/images/upload`, formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .then(() => {
                        refreshPosts({
                            username: authState.account?.username,
                            targetUsername: targetUsername,
                            followedOnly,
                            onDone: () => {
                                resetPostInput();
                                setPosting(false);
                            },
                        });
                    });
            });
    }, [
        postRecipes,
        postInputText,
        visibility,
        files,
        refreshPosts,
        authState.account?.username,
        targetUsername,
        followedOnly,
        resetPostInput,
    ]);

    // Render post images on post input //
    const renderedPostImages = useMemo(
        () =>
            postImages.map((postImage) => {
                return (
                    <Card sx={{ p: 1, maxWidth: 200, position: "relative" }}>
                        <IconButton onClick={removeImage} sx={{ position: "absolute", top: 0, right: 0 }}>
                            <Clear />
                        </IconButton>
                        <Box
                            component="img"
                            src={postImage}
                            alt="Preview"
                            sx={{
                                width: "100%",
                                height: "auto",
                                objectFit: "contain",
                                display: "block",
                                backgroundColor: "#f0f0f0",
                            }}
                        />
                    </Card>
                );
            }),
        [postImages, removeImage]
    );

    // Render post recipes on post input (default view) //
    const renderedPostRecipes = useMemo(
        () =>
            postRecipes.map((recipe) => (
                <Tooltip key={recipe._id} title={recipe.description || ""}>
                    <Chip label={recipe.name} onClick={() => {}} onDelete={() => removeRecipe(recipe._id)} sx={{ m: 0.5 }} />
                </Tooltip>
            )),
        [postRecipes, removeRecipe]
    );

    // Render post recipes on post input (accordian view) //
    const renderedPostRecipesAccordian = useMemo(
        () =>
            postRecipes.map((recipe) => (
                <Box key={recipe._id} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ flex: 1 }}>{recipe.name}</Typography>
                    <IconButton size="small" onClick={() => removeRecipe(recipe._id)}>
                        <Clear fontSize="small" />
                    </IconButton>
                </Box>
            )),
        [postRecipes, removeRecipe]
    );

    return (
        <Box sx={{ width: "100%", height: "100%" }}>
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    padding: 2,
                    alignItems: "center",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%",
                        maxWidth: 1200,
                    }}
                >
                    {/* Post input -- Show only if the user is logged in */}
                    {targetUsername ? (
                        <Box sx={{ width: "100%", flexShrink: 0 }}>
                            <Paper
                                elevation={3}
                                sx={{
                                    borderRadius: "16px",
                                    gap: 2,
                                    width: "100%",
                                    bgcolor: "background.paper",
                                    overflow: "visible",
                                }}
                            >
                                <Box
                                    sx={{
                                        flexDirection: { xs: "column", sm: "row" },
                                        pb: { xs: 0.5, sm: 0 },
                                        display: "flex",
                                        alignItems: "center",
                                        px: { xs: 0, sm: 2 },
                                        borderRadius: "16px",
                                        gap: 2,
                                        width: "100%",
                                        bgcolor: "background.paper",
                                        overflow: "visible",
                                    }}
                                >
                                    {}
                                    {!isXs && <Avatar alt="You" src={authState.account?.image} sx={{ width: 40, height: 40 }} />}

                                    {/* Post input textfield */}
                                    <PostInput value={postInputText} onChange={handlePostInputChange} />

                                    {/* Post Visibility drop down */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {visibilityLoading ? (
                                            <Loading size="16px" />
                                        ) : (
                                            <Select
                                                labelId="visibility-label"
                                                id="visibility"
                                                value={visibility}
                                                label="Visibility"
                                                onChange={(e) => setVisibility(e.target.value)}
                                                sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    borderRadius: "18px",
                                                    height: "36px",
                                                    color: "white",
                                                }}
                                                renderValue={(selectedValue) => {
                                                    const selectedOption = Object.values(visibilityOptions).find(
                                                        (option) => option.value === selectedValue
                                                    );
                                                    return selectedOption ? selectedOption.label : "";
                                                }}
                                            >
                                                {Object.values(visibilityOptions).map(({ value, label, Icon }) => (
                                                    <MenuItem
                                                        value={value}
                                                        key={value}
                                                        sx={{
                                                            py: 1.25,
                                                            "&.Mui-selected": { bgcolor: "action.selected" },
                                                            "& .MuiListItemText-primary": { fontWeight: 500 },
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            <Icon sx={{ color: theme.palette.primary.main }} fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={label}
                                                            secondary={visibilityLabels[value as keyof typeof visibilityLabels]}
                                                        />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}

                                        {/* Post input icons */}
                                        <IconButton size="small" color="primary" onClick={handleEmojiPicker}>
                                            <InsertEmoticon />
                                        </IconButton>
                                        <Tooltip title="Add Recipe" placement="top">
                                            <IconButton size="small" color="primary" onClick={() => setOpenRecipeUploadModal(true)}>
                                                <MenuBook />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Upload Image" placement="top">
                                            <IconButton size="small" color="primary" onClick={() => setOpenImageUploadModal(true)}>
                                                <Image />
                                            </IconButton>
                                        </Tooltip>

                                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                                        {/* Post submit button */}
                                        <LoadingButton
                                            loading={posting}
                                            disabled={!postInputText.trim()}
                                            onClick={createPost}
                                            label="Post"
                                        />
                                    </Box>
                                </Box>

                                {/* Post Recipes -- Show default if 1-3 recipes, show accordian if 3+  */}
                                {postRecipes.length > 0 && postRecipes.length > 3 ? (
                                    <Box sx={{ px: 2 }}>
                                        <Accordion elevation={0} disableGutters>
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Typography variant="body2">{postRecipes.length} recipe(s)</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>{renderedPostRecipesAccordian}</AccordionDetails>
                                        </Accordion>
                                    </Box>
                                ) : (
                                    renderedPostRecipes
                                )}

                                {/* Post Images */}
                                {postImages.length > 0 && <Box sx={{ display: "flex" }}>{renderedPostImages}</Box>}
                            </Paper>
                        </Box>
                    ) : (
                        <Box sx={{ position: "absolute" }}>
                            {/* If not logged in, show indicator to log in. */}
                            <Typography>
                                Want to join the conversation?{" "}
                                <Typography variant="body2" color="text.secondary" component="a" sx={{ cursor: "pointer" }} href="/login">
                                    Log in!
                                </Typography>
                            </Typography>
                        </Box>
                    )}

                    {/* Post card feed */}
                    <MemoizedPostCardFeed
                        followedOnly={followedOnly}
                        setFollowedOnly={setFollowedOnly}
                        feedLoading={feedLoading}
                        feedSize={"calc(100dvh - 250px)"}
                        setFeedLoading={setFeedLoading}
                    />
                </Box>
            </Box>

            {/* Upload image / recipes modal */}
            <Dialog
                maxWidth={false}
                open={openImageUploadModal || openRecipeUploadModal}
                onClose={() => {
                    setOpenImageUploadModal(false);
                    setOpenRecipeUploadModal(false);
                }}
            >
                {openImageUploadModal && (
                    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                        <Typography variant="h5" sx={{ textAlign: "center" }}>
                            Please select an image to post!
                        </Typography>
                        <ImageUpload type="Post" handleImageAdd={addImage} />
                    </Box>
                )}
                {openRecipeUploadModal && <RecipePicker handlePostRecipes={addRecipes} postRecipes={postRecipes} />}
            </Dialog>

            {/* Emoji Picker */}
            <Popover
                open={Boolean(emojiPicker)}
                anchorEl={emojiPicker}
                onClose={handleCloseEmojiPicker}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                disableAutoFocus
                disableEnforceFocus
            >
                {emojiPickerElement}
            </Popover>
        </Box>
    );
};

export default CommunityFeed;
